// Iter101 γ-3-I — SITREP AI intake chat page for Freight Deals.
//
// Conversational entry point — the user describes the freight need in
// chat, Claude Haiku 4.5 extracts the 7 main fields, asks for what's
// missing, and on confirm we POST /ai-intake/freight_deals/create →
// redirect to the standard editor pre-filled with the draft.
//
// Designed to be reusable: the context is hard-coded to `freight_deals`
// here, but the page reads its intro + field-set from the server so
// a customer-portal variant (different context) will Just Work.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Sparkles, ArrowLeft, Check, AlertCircle, RotateCw } from "lucide-react";
import { useT } from "../lib/i18n";
import { useAiIntakeIntro, useAiIntakeChat, useAiIntakeCreate } from "../api/aiIntake";
import { toast } from "sonner";

const CONTEXT = "freight_deals";

const PAGE_TITLE_I18N = {
  en: "SITREP · AI Freight-Deal intake",
  de: "SITREP · KI-Frachtauftrag-Aufnahme",
  hu: "SITREP · AI Fuvarmegbízás-felvétel",
};
const HINT_I18N = {
  en: "Describe what you're shipping — route, customer, cargo, dates. I'll fill in a draft and you finish it in the editor.",
  de: "Erzähl mir was du transportieren willst — Route, Kunde, Fracht, Termine. Ich erstelle einen Entwurf, den du im Editor fertigstellst.",
  hu: "Írd le, mit szállítasz — útvonal, ügyfél, áru, dátumok. Készítek egy vázlatot, te befejezed a szerkesztőben.",
};
const CREATE_BTN_I18N = {
  en: "Create draft & continue in editor →",
  de: "Entwurf erstellen & im Editor weiter →",
  hu: "Vázlat létrehozása & szerkesztőben tovább →",
};
const SEND_BTN_I18N = { en: "Send", de: "Senden", hu: "Küldés" };
const MISSING_LABEL_I18N = {
  en: "Still missing",
  de: "Noch fehlend",
  hu: "Még hiányzik",
};
const READY_LABEL_I18N = {
  en: "Ready to create",
  de: "Bereit zum Erstellen",
  hu: "Készen áll a létrehozásra",
};
const MIN_READY_I18N = {
  en: "Minimum met — you can create now or keep going",
  de: "Minimum erfüllt — jetzt erstellen oder weitermachen",
  hu: "Minimum megvan — létrehozhatod most, vagy folytasd",
};

const FIELD_LABEL_I18N = {
  deal_name: { en: "Deal name", de: "Auftragsname", hu: "Megnevezés" },
  request_type: { en: "Type", de: "Typ", hu: "Típus" },
  customer_name: { en: "Customer", de: "Kunde", hu: "Ügyfél" },
  pickup: { en: "Pickup", de: "Abholung", hu: "Felvétel" },
  dropoff: { en: "Dropoff", de: "Zustellung", hu: "Leszállítás" },
  cargo: { en: "Cargo", de: "Fracht", hu: "Áru" },
  celar: { en: "Target price", de: "Zielpreis", hu: "Célár" },
};

function _t(map, locale) {
  return map?.[locale] || map?.en || "";
}

function _fmtField(key, value, locale) {
  if (value == null) return "—";
  if (key === "pickup" || key === "dropoff") {
    const c = [value.city, value.country].filter(Boolean).join(", ");
    const t = value.window_start_at || value.window_end_at;
    return [c, t ? String(t).slice(0, 16).replace("T", " ") : null].filter(Boolean).join(" · ") || "—";
  }
  if (key === "cargo") {
    return [value.aru_megnevezese, value.autotipus, value.suly_kg && `${value.suly_kg} kg`]
      .filter(Boolean).join(" · ") || "—";
  }
  return String(value);
}

export default function LogisticsIntakeChat() {
  const navigate = useNavigate();
  const { locale } = useT();
  const intro = useAiIntakeIntro(CONTEXT, locale);
  const chat = useAiIntakeChat(CONTEXT);
  const createDraft = useAiIntakeCreate(CONTEXT);

  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [extracted, setExtracted] = useState({});
  const [missing, setMissing] = useState([]);
  const [minimumMet, setMinimumMet] = useState(false);
  const [readyToCreate, setReadyToCreate] = useState(false);
  const scrollRef = useRef(null);

  // Surface the assistant's intro as the first message (once intro loads).
  useEffect(() => {
    if (intro.data?.intro && messages.length === 0) {
      setMessages([{ role: "assistant", content: intro.data.intro }]);
    }
  }, [intro.data?.intro]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || chat.isPending) return;
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    try {
      const resp = await chat.mutateAsync({
        messages: next.map((m) => ({ role: m.role, content: m.content })),
        locale,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: resp.assistant_message || "…" }]);
      setExtracted(resp.extracted || {});
      setMissing(resp.missing || []);
      setMinimumMet(!!resp.minimum_met);
      setReadyToCreate(!!resp.ready_to_create);
      // γ-3-I refinement — user said "create it now / hozd létre most /
      // erstelle es jetzt" (server-detected) → auto-fire the create call
      // with whatever we have so far. The minimum-met guard is already
      // enforced server-side: force_create is only true when minimum_met
      // is also true.
      if (resp.force_create) {
        // Use the freshly-extracted state, not the (stale) component state.
        await _createWithExtracted(resp.extracted || {});
      }
    } catch (e) {
      toast.error("AI assistant temporarily unavailable. Try again or use the standard form.");
    }
  };

  const _createWithExtracted = async (extr) => {
    try {
      const created = await createDraft.mutateAsync(extr);
      toast.success(`Draft ${created.id} created. Complete it in the editor.`);
      navigate(`/logistics-requests/${created.id}/edit`);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.message || "Failed to create draft";
      toast.error(msg);
    }
  };

  const onCreate = () => _createWithExtracted(extracted);

  const reset = () => {
    setMessages(intro.data?.intro ? [{ role: "assistant", content: intro.data.intro }] : []);
    setExtracted({});
    setMissing([]);
    setMinimumMet(false);
    setReadyToCreate(false);
    setInput("");
  };

  const fieldKeys = Object.keys(FIELD_LABEL_I18N);
  // γ-3-I refinement — gate Create on minimum_met (deal_name + request_type
  // + ANY of customer/pickup/dropoff). Was: missing.length === 0 (required
  // ALL 6 main fields). Now the user can finalize as soon as the minimum
  // exists and continue the rest manually in the editor.
  const canCreate = minimumMet;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="logistics-intake-chat-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/logistics-requests")}
            className="inline-flex items-center gap-1 px-2 py-1 rounded"
            style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.15)", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}
            data-testid="logistics-intake-back"
          >
            <ArrowLeft size={13} /> Back
          </button>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={20} style={{ color: "#7e3aa8" }} /> {_t(PAGE_TITLE_I18N, locale)}
          </h1>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1 px-2 py-1 rounded"
          style={{ background: "transparent", border: "1px solid rgba(0,0,0,0.15)", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer" }}
          data-testid="logistics-intake-reset"
          title="Start over"
        >
          <RotateCw size={12} /> Reset
        </button>
      </div>
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dimmer)", marginBottom: 12 }}>
        {_t(HINT_I18N, locale)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* ─────────── Chat column ─────────── */}
        <div className="rounded flex flex-col" style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))", height: "calc(100vh - 220px)", minHeight: 480 }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="logistics-intake-chat-log">
            {messages.map((m, i) => (
              <div
                key={i}
                className="flex"
                style={{ justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}
              >
                <div
                  className="rounded-lg px-3 py-2"
                  style={{
                    maxWidth: "78%",
                    background: m.role === "user" ? "#185FA5" : "rgba(126,58,168,0.10)",
                    color: m.role === "user" ? "#ffffff" : "var(--text, #0d1220)",
                    border: m.role === "assistant" ? "1px solid rgba(126,58,168,0.25)" : "none",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.45,
                  }}
                  data-testid={`logistics-intake-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {chat.isPending && (
              <div className="flex justify-start" data-testid="logistics-intake-pending">
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#7e3aa8", padding: "4px 10px", background: "rgba(126,58,168,0.08)", borderRadius: 6 }}>
                  thinking…
                </div>
              </div>
            )}
          </div>
          <div className="p-3" style={{ borderTop: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
            <div className="flex items-center gap-2">
              <textarea
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={_t(HINT_I18N, locale)}
                className="flex-1 px-3 py-2 rounded"
                style={{ background: "var(--bg, #f6f7fb)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))", fontFamily: "'DM Mono', monospace", fontSize: 12, resize: "none", outline: "none" }}
                data-testid="logistics-intake-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={chat.isPending || !input.trim()}
                className="inline-flex items-center gap-1 px-3 py-2 rounded"
                style={{ background: chat.isPending || !input.trim() ? "#9198b0" : "#185FA5", color: "#ffffff", border: "none", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, cursor: chat.isPending || !input.trim() ? "not-allowed" : "pointer" }}
                data-testid="logistics-intake-send"
              >
                <Send size={12} /> {_t(SEND_BTN_I18N, locale)}
              </button>
            </div>
          </div>
        </div>

        {/* ─────────── Live extraction panel ─────────── */}
        <div className="rounded sticky top-4 self-start" style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.12))" }} data-testid="logistics-intake-summary-panel">
          <div className="px-3 py-2" style={{ borderBottom: "1px solid var(--border-strong, rgba(0,0,0,0.10))", background: "rgba(126,58,168,0.06)" }}>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#7e3aa8", letterSpacing: "0.4px", textTransform: "uppercase" }}>
              Extracted so far
            </div>
          </div>
          <div className="p-3 space-y-2">
            {fieldKeys.map((k) => {
              const val = extracted[k];
              const isMissing = missing.includes(k);
              return (
                <div
                  key={k}
                  className="flex items-baseline justify-between gap-2 py-1"
                  style={{ borderBottom: "1px dashed rgba(0,0,0,0.06)" }}
                  data-testid={`logistics-intake-field-${k}`}
                >
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "0.3px", flex: "0 0 36%", textTransform: "uppercase" }}>
                    {_t(FIELD_LABEL_I18N[k], locale)}
                  </span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: isMissing ? "#b22a29" : (val ? "#0a7a3c" : "var(--text-dimmer)"), flex: 1, textAlign: "right", fontWeight: val ? 700 : 400 }}>
                    {_fmtField(k, val, locale)}
                  </span>
                </div>
              );
            })}
            {missing.length > 0 && !minimumMet && (
              <div className="mt-2 px-2 py-1.5 rounded flex items-center gap-2" style={{ background: "rgba(178,42,41,0.08)", border: "1px solid rgba(178,42,41,0.35)" }} data-testid="logistics-intake-missing-banner">
                <AlertCircle size={12} style={{ color: "#b22a29" }} />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#b22a29" }}>
                  {_t(MISSING_LABEL_I18N, locale)}: {missing.map((k) => _t(FIELD_LABEL_I18N[k], locale)).join(", ")}
                </span>
              </div>
            )}
            {minimumMet && !readyToCreate && (
              <div className="mt-2 px-2 py-1.5 rounded flex items-center gap-2" style={{ background: "rgba(183,119,13,0.10)", border: "1px solid rgba(183,119,13,0.40)" }} data-testid="logistics-intake-min-ready-banner">
                <Check size={12} style={{ color: "#b7770d" }} />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#b7770d", fontWeight: 700 }}>
                  {_t(MIN_READY_I18N, locale)}
                </span>
              </div>
            )}
            {readyToCreate && (
              <div className="mt-2 px-2 py-1.5 rounded flex items-center gap-2" style={{ background: "rgba(10,122,60,0.08)", border: "1px solid rgba(10,122,60,0.35)" }} data-testid="logistics-intake-ready-banner">
                <Check size={12} style={{ color: "#0a7a3c" }} />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#0a7a3c", fontWeight: 700 }}>
                  {_t(READY_LABEL_I18N, locale)}
                </span>
              </div>
            )}
            <button
              type="button"
              disabled={!canCreate || createDraft.isPending}
              onClick={onCreate}
              className="w-full mt-3 inline-flex items-center justify-center gap-1 px-3 py-2 rounded"
              style={{ background: !canCreate ? "#9198b0" : "#0a7a3c", color: "#ffffff", border: "none", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.3px", cursor: !canCreate ? "not-allowed" : "pointer" }}
              data-testid="logistics-intake-create"
            >
              <Sparkles size={12} /> {createDraft.isPending ? "Creating…" : _t(CREATE_BTN_I18N, locale)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
